import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Plus, Percent, Gift, Truck, Tag,
  Calendar, Clock, Trash2, Edit2, ToggleLeft, ToggleRight,
  Package, Layers, ShoppingBag, ChevronRight, Zap, Target
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/PremiumComponents";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import { API_URL } from "@/utils/api";
import AdminLayout from "@/components/layouts/AdminLayout";

const DAYS_OF_WEEK = [
  { value: 0, label: "Lunes", short: "L" },
  { value: 1, label: "Martes", short: "M" },
  { value: 2, label: "Miércoles", short: "X" },
  { value: 3, label: "Jueves", short: "J" },
  { value: 4, label: "Viernes", short: "V" },
  { value: 5, label: "Sábado", short: "S" },
  { value: 6, label: "Domingo", short: "D" },
];

const DISCOUNT_TYPES = [
  { value: "percentage", label: "Porcentaje", icon: Percent, color: "text-blue-400" },
  { value: "2x1", label: "2x1 / Regalo", icon: Gift, color: "text-purple-400" },
  { value: "fixed_price", label: "Precio fijo", icon: Tag, color: "text-green-400" },
  { value: "free_delivery", label: "Envío gratis", icon: Truck, color: "text-orange-400" },
];

const APPLY_TO_OPTIONS = [
  { value: "all", label: "Todo el menú", icon: ShoppingBag },
  { value: "category", label: "Categorías", icon: Layers },
  { value: "products", label: "Productos", icon: Package },
];

export default function AdminOffers() {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    discount_type: "percentage",
    discount_value: 0,
    apply_to: "all",
    category_ids: [],
    product_ids: [],
    active_days: [0, 1, 2, 3, 4, 5, 6],
    start_time: "",
    end_time: "",
    active: false,
    image: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [offersRes, categoriesRes, productsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/offers`, { withCredentials: true }),
        axios.get(`${API_URL}/categories`),
        axios.get(`${API_URL}/menu`)
      ]);
      setOffers(offersRes.data);
      setCategories(categoriesRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      toast.error("Error al cargar ofertas");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (offer = null) => {
    if (offer) {
      setEditingOffer(offer);
      setFormData({ ...offer });
    } else {
      setEditingOffer(null);
      setFormData({
        name: "", description: "", discount_type: "percentage", discount_value: 0,
        apply_to: "all", category_ids: [], product_ids: [], active_days: [0, 1, 2, 3, 4, 5, 6],
        start_time: "", end_time: "", active: true, image: ""
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editingOffer) {
        await axios.put(`${API_URL}/admin/offers/${editingOffer.offer_id}`, formData, { withCredentials: true });
        toast.success("Oferta actualizada");
      } else {
        await axios.post(`${API_URL}/admin/offers`, formData, { withCredentials: true });
        toast.success("Oferta creada");
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Error al guardar oferta");
    }
  };

  const handleToggleActive = async (offer) => {
    try {
      await axios.put(`${API_URL}/admin/offers/${offer.offer_id}/toggle`, {}, { withCredentials: true });
      fetchData();
    } catch (error) { toast.error("Error al cambiar estado"); }
  };

  const handleDelete = async (id) => {
    if (confirm("¿Eliminar oferta?")) {
      try {
        await axios.delete(`${API_URL}/admin/offers/${id}`, { withCredentials: true });
        fetchData();
      } catch (error) { toast.error("Error al eliminar"); }
    }
  };

  if (isLoading) return <div className="min-h-screen bg-black flex items-center justify-center text-yellow-500 animate-pulse font-black uppercase tracking-tighter italic">Cargando Ofertas...</div>;

  return (
    <AdminLayout title="Fidelización y Ofertas Premium">
      <div className="space-y-10 animate-in fade-in duration-500 font-sans pb-20">
        
        {/* HEADER SECTION */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-1">
             <h2 className="text-3xl font-black text-white italic tracking-tighter uppercase flex items-center gap-3">
               Catálogo de <span className="text-yellow-500">Ofertas</span>
             </h2>
             <p className="text-gray-500 text-xs font-bold uppercase tracking-widest">Optimiza tus ventas con promociones estratégicas</p>
          </div>
          <Button 
            onClick={() => handleOpenDialog()}
            className="bg-yellow-500 hover:bg-yellow-600 text-black font-black uppercase text-xs h-12 px-8 rounded-2xl shadow-[0_15px_40px_rgba(234,179,8,0.3)] transition-all active:scale-95"
          >
            <Plus className="w-5 h-5 mr-2" /> Nueva Promo
          </Button>
        </div>

        {/* OFFERS GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {offers.map(offer => {
            const type = DISCOUNT_TYPES.find(t => t.value === offer.discount_type) || DISCOUNT_TYPES[0];
            const Icon = type.icon;
            
            return (
              <div 
                key={offer.offer_id}
                className={`group relative h-full flex flex-col bg-white/[0.02] border ${offer.active ? 'border-white/5' : 'border-red-900/20 grayscale'} rounded-[2.5rem] overflow-hidden transition-all hover:bg-white/[0.04] hover:border-yellow-500/30 p-8 shadow-2xl`}
              >
                {/* Status Badge */}
                <div className="absolute top-6 right-6">
                  <Badge variant={offer.active ? "success" : "danger"}>
                    {offer.active ? "ACTIVA" : "PAUSA"}
                  </Badge>
                </div>

                {/* Offer Header */}
                <div className="flex items-center gap-4 mb-8">
                  <div className={`w-14 h-14 rounded-2xl bg-black/40 border border-white/5 flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform ${type.color}`}>
                    <Icon className="w-7 h-7" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-black text-white uppercase tracking-tighter truncate italic">{offer.name}</h3>
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest leading-none">Promo Exp. {offer.active_days.length} días</p>
                  </div>
                </div>

                {/* Offer Body */}
                <div className="flex-1">
                   <div className="bg-gradient-to-br from-yellow-500 to-amber-600 p-6 rounded-[2rem] shadow-[0_15px_30px_rgba(0,0,0,0.4)] mb-6 transform -rotate-2 group-hover:rotate-0 transition-transform">
                      <span className="text-[10px] font-black text-black/50 uppercase tracking-tighter block mb-1">Ahorro Directo</span>
                      <span className="text-4xl font-black text-black tracking-tighter italic">
                         {offer.discount_type === 'percentage' ? `${offer.discount_value}% OFF` : 
                          offer.discount_type === '2x1' ? '2x1' : 
                          offer.discount_type === 'fixed_price' ? `€${offer.discount_value.toFixed(2)}` : 'FREE SHIP'}
                      </span>
                   </div>
                   <p className="text-gray-400 text-sm italic font-medium leading-relaxed bg-black/20 p-4 rounded-2xl border border-white/5">
                      "{offer.description}"
                   </p>
                </div>

                {/* Footer Controls */}
                <div className="mt-8 pt-8 border-t border-white/5 flex items-center justify-between">
                   <div className="flex gap-1.5 font-black text-[9px] text-gray-500 uppercase tracking-tight">
                      {offer.active_days.map(d => DAYS_OF_WEEK.find(day => day.value === d)?.short).join('/')}
                   </div>
                   <div className="flex items-center gap-2">
                      <button onClick={() => handleToggleActive(offer)} className="p-3 bg-white/5 border border-white/5 rounded-xl hover:text-yellow-500 transition-colors">
                        {offer.active ? <ToggleRight className="w-6 h-6" /> : <ToggleLeft className="w-6 h-6" />}
                      </button>
                      <button onClick={() => handleOpenDialog(offer)} className="p-3 bg-white/5 border border-white/5 rounded-xl hover:text-blue-400">
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button onClick={() => handleDelete(offer.offer_id)} className="p-3 bg-white/5 border border-white/5 rounded-xl hover:text-red-500">
                        <Trash2 className="w-5 h-5" />
                      </button>
                   </div>
                </div>
              </div>
            );
          })}

          {offers.length === 0 && (
            <div className="col-span-full py-20 text-center bg-white/[0.01] border-2 border-dashed border-white/5 rounded-[3rem]">
               <Zap className="w-16 h-16 text-gray-700 mx-auto mb-6 opacity-20" />
               <p className="text-gray-500 font-black uppercase tracking-widest text-sm">No tienes ofertas vigentes</p>
               <Button variant="link" className="text-yellow-500 mt-2 font-black uppercase italic" onClick={() => handleOpenDialog()}>Inaugura tu primera promo</Button>
            </div>
          )}
        </div>
      </div>

      {/* FORM DIALOG */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-gray-900 border-white/10 text-white max-w-2xl rounded-[3rem] p-0 overflow-hidden shadow-[0_50px_100px_rgba(0,0,0,0.8)] border shadow-black font-sans">
          <div className="bg-yellow-500 p-8 flex justify-between items-center bg-gradient-to-r from-yellow-500 to-amber-600">
             <div>
               <h3 className="text-2xl font-black text-black italic tracking-tighter uppercase">{editingOffer ? 'Optimizar' : 'Crear'} Oferta</h3>
               <p className="text-black/60 text-[10px] font-black uppercase tracking-widest">Configuración Maestra</p>
             </div>
             <div className="bg-black/10 p-4 rounded-3xl">
               <Target className="w-8 h-8 text-black" />
             </div>
          </div>
          <div className="p-10 space-y-8 max-h-[70vh] overflow-y-auto scrollbar-hide">
            
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-[10px] font-black text-gray-500 uppercase tracking-widest px-1">Título de la Promo</Label>
                <Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value.toUpperCase()})} className="bg-white/5 border-white/10 h-12 rounded-2xl focus:ring-yellow-500/20 italic font-black" />
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] font-black text-gray-500 uppercase tracking-widest px-1">Tipo de Descuento</Label>
                <Select value={formData.discount_type} onValueChange={val => setFormData({...formData, discount_type: val})}>
                   <SelectTrigger className="bg-white/5 border-white/10 h-12 rounded-2xl"><SelectValue /></SelectTrigger>
                   <SelectContent className="bg-gray-800 text-white border-white/10 font-bold uppercase text-[10px]">
                      {DISCOUNT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                   </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[10px] font-black text-gray-500 uppercase tracking-widest px-1">Descripción / Claim</Label>
              <Textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} className="bg-white/5 border-white/10 rounded-2xl focus:ring-yellow-500/20 italic font-medium" placeholder="EJ: Solo mañana, pide tu burger y lleva los tacos gratis" />
            </div>

            <div className="grid grid-cols-2 gap-6">
               <div className="space-y-2">
                 <Label className="text-[10px] font-black text-gray-500 uppercase tracking-widest px-1">Valor Promo</Label>
                 <Input type="number" value={formData.discount_value} onChange={e => setFormData({...formData, discount_value: parseFloat(e.target.value)})} className="bg-white/5 border-white/10 h-12 rounded-2xl font-black text-xl" />
               </div>
               <div className="space-y-2">
                 <Label className="text-[10px] font-black text-gray-500 uppercase tracking-widest px-1">Scope</Label>
                 <Select value={formData.apply_to} onValueChange={val => setFormData({...formData, apply_to: val, category_ids: [], product_ids: []})}>
                   <SelectTrigger className="bg-white/5 border-white/10 h-12 rounded-2xl"><SelectValue /></SelectTrigger>
                   <SelectContent className="bg-gray-800 text-white border-white/10 font-bold uppercase text-[10px]">
                      {APPLY_TO_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                   </SelectContent>
                </Select>
               </div>
            </div>

            <div className="space-y-4">
               <Label className="text-[10px] font-black text-gray-500 uppercase tracking-widest px-1">Días de Activación</Label>
               <div className="flex gap-2">
                  {DAYS_OF_WEEK.map(day => {
                    const active = formData.active_days.includes(day.value);
                    return (
                      <button 
                         key={day.value}
                         onClick={() => setFormData({...formData, active_days: active ? formData.active_days.filter(d => d !== day.value) : [...formData.active_days, day.value]})}
                         className={`w-10 h-10 rounded-xl font-black text-[10px] transition-all border ${active ? 'bg-yellow-500 text-black border-yellow-500 shadow-lg shadow-yellow-500/20' : 'bg-white/5 text-gray-400 border-white/5'}`}
                      >
                         {day.short}
                      </button>
                    )
                  })}
               </div>
            </div>

            <div className="flex items-center justify-between p-6 bg-white/[0.02] border border-white/5 rounded-[2rem]">
               <div>
                  <h4 className="font-black text-white text-xs uppercase italic tracking-tighter">Publicar Inmediatamente</h4>
                  <p className="text-[9px] font-bold text-gray-500 uppercase tracking-widest">La oferta será visible para todos los clientes</p>
               </div>
               <Switch checked={formData.active} onCheckedChange={checked => setFormData({...formData, active: checked})} />
            </div>

            <div className="flex gap-4 pt-4 pb-10">
               <Button className="flex-1 bg-white text-black font-black uppercase italic rounded-2xl h-14 hover:scale-105 active:scale-95 transition-transform" onClick={handleSubmit}>Guardar Estrategia</Button>
               <Button variant="ghost" className="text-gray-500 uppercase font-black text-[9px] tracking-widest" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
